import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getCurrentUser,
  getProfile,
  updateProfile,
  updatePassword,
  uploadAvatar,
  updateAvatarUrl,
  deleteAccount,
  getGroups,
  hasCompletedBaselineAssessment,
} from '@/lib/api';

// Query keys for profile data
export const profileKeys = {
  all: ['profile'] as const,
  user: () => [...profileKeys.all, 'user'] as const,
  profile: (userId: string) => [...profileKeys.all, 'data', userId] as const,
  groups: () => [...profileKeys.all, 'groups'] as const,
  baselineAssessmentCompletion: (productId: string) => [...profileKeys.all, 'baseline-completion', productId] as const,
};

// Types
export interface ProfileData {
  // Name fields
  first_name: string;
  last_name: string;
  // Contact fields
  phone: string;
  country_code: string;
  // Address fields
  street_address: string;
  street_address_line_2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  // Personal information
  gender: string;
  date_of_birth: string;
  // Other fields
  avatar_url: string;
  preferred_language: string;
  group_id: string;
  subgroup_id: string;
  primary_product_id: string;
  invite_code_used: string;
}

export interface UserData {
  id: string;
  email: string;
  user_metadata?: {
    signup_method?: string;
  };
}

// Hook to get current user
export function useCurrentUser() {
  return useQuery({
    queryKey: profileKeys.user(),
    queryFn: async (): Promise<UserData | null> => {
      const result = await getCurrentUser();
      if (!result.success) return null;
      return result.data as UserData;
    },
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}

// Hook to get user profile
export function useProfile(userId: string | undefined) {
  return useQuery({
    queryKey: profileKeys.profile(userId || ''),
    queryFn: async (): Promise<ProfileData> => {
      if (!userId) throw new Error('No user ID');
      const result = await getProfile(userId);
      if (!result.success || !result.data) {
        throw new Error('Failed to fetch profile');
      }
      const data = result.data;
      return {
        // Name fields
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        // Contact fields
        phone: data.phone || '',
        country_code: data.country_code || '+1',
        // Address fields
        street_address: data.street_address || '',
        street_address_line_2: data.street_address_line_2 || '',
        city: data.city || '',
        state: data.state || '',
        zip: data.zip || '',
        country: data.country || '',
        // Personal information
        gender: data.gender || '',
        date_of_birth: data.date_of_birth || '',
        // Other fields
        avatar_url: data.avatar_url || '',
        preferred_language: data.preferred_language || '',
        group_id: data.group_id || '',
        subgroup_id: data.subgroup_id || '',
        primary_product_id: data.primary_product_id || '',
        invite_code_used: data.invite_code_used || '',
      };
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 5,
  });
}

// Hook to get groups
export function useGroups() {
  return useQuery({
    queryKey: profileKeys.groups(),
    queryFn: async () => {
      const result = await getGroups();
      if (!result.success) throw new Error('Failed to fetch groups');
      return result.data;
    },
    staleTime: 1000 * 60 * 10,
  });
}

// Hook to check if user has completed baseline assessment for a product
export function useBaselineAssessmentCompletion(userId: string | undefined, productId: string) {
  return useQuery({
    queryKey: profileKeys.baselineAssessmentCompletion(productId),
    queryFn: async (): Promise<boolean> => {
      if (!userId) return false;
      const result = await hasCompletedBaselineAssessment(userId, productId);
      return result.success ? result.data : false;
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Custom error class for profile updates
export class ProfileUpdateError extends Error {
  code: string;
  status: number;

  constructor(message: string, code: string, status: number) {
    super(message);
    this.name = 'ProfileUpdateError';
    this.code = code;
    this.status = status;
  }
}

// Mutation to update profile
export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, profile }: { userId: string; profile: Partial<ProfileData> }) => {
      const result = await updateProfile(userId, profile as any);
      if ('error' in result && result.error) {
        throw new ProfileUpdateError(
          result.error.message || 'Failed to update profile',
          result.error.code || 'UNKNOWN_ERROR',
          result.error.status || 500
        );
      }
      if (!result.success) {
        throw new ProfileUpdateError('Failed to update profile', 'UNKNOWN_ERROR', 500);
      }
      
      // Sync phone to auth.users for SMS OTP login support
      if (profile.phone !== undefined && profile.country_code !== undefined) {
        const { supabase } = await import('@/integrations/supabase/client');
        const fullPhone = profile.phone 
          ? `${profile.country_code}${profile.phone.replace(/\D/g, '')}` 
          : null;
        
        // Update auth.users.phone (required for SMS OTP passwordless login)
        const { error: authError } = await supabase.auth.updateUser({
          phone: fullPhone || '',
        });
        
        if (authError) {
          console.warn('Failed to sync phone to auth.users:', authError.message);
          // Don't fail the profile update if auth sync fails
        }
      }
      
      return result.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: profileKeys.profile(variables.userId) });
    },
  });
}

// Mutation to update password
export function useUpdatePassword() {
  return useMutation({
    mutationFn: async (newPassword: string) => {
      const result = await updatePassword(newPassword);
      if (!result.success) throw new Error('Failed to update password');
      return result;
    },
  });
}

// Mutation to upload avatar
export function useUploadAvatar() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, file }: { userId: string; file: File }) => {
      const uploadResult = await uploadAvatar(userId, file);
      if (!uploadResult.success) throw new Error('Failed to upload avatar');
      
      const publicUrl = uploadResult.data;
      const updateResult = await updateAvatarUrl(userId, publicUrl);
      if (!updateResult.success) throw new Error('Failed to update avatar URL');
      
      return publicUrl;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: profileKeys.profile(variables.userId) });
    },
  });
}

// Mutation to delete account
export function useDeleteAccount() {
  return useMutation({
    mutationFn: async () => {
      const result = await deleteAccount();
      if (!result.success) throw new Error('Failed to delete account');
      return result;
    },
  });
}
